// Copyright (c) StrangeBee 2024

import { useEffect, useState } from 'react';
import { ObjectTypeV1 } from 'thehive-sdk';
import { Storage } from '@Services/Storage';

export const defaultFilters = Object.values(ObjectTypeV1).reduce(
    (acc, value) => ({ ...acc, [value]: [] }),
    {},
) as Record<ObjectTypeV1, []>;

export const defaultKeywords = Object.values(ObjectTypeV1).reduce(
    (acc, value) => ({ ...acc, [value]: '' }),
    {},
) as Record<ObjectTypeV1, string>;

export enum LocalStorageKeys {
    PAGE = 'page',
    SEARCH = 'search',
    PINS = 'pins',
    TIMELINE = 'timeline',
    VIEW = 'view',
    TABS = 'tabs',
    LICENSE_REMINDER = 'license_reminder',
    LANGUAGE_SWITCHER = 'language-switcher',
    OPTIONS = 'options',
    COLLAPSE = 'collapse',
}

interface UseLocalStorageParams<T> {
    key: LocalStorageKeys;
    initialValue: T | ((value: T | null) => T);
}

interface UseLocalStorageReturn<T> {
    storage: T;
    setStorage: React.Dispatch<React.SetStateAction<T>>;
}

const useLocalStorage = <T>({ key, initialValue }: UseLocalStorageParams<T>): UseLocalStorageReturn<T> => {
    const [storage, setStorage] = useState<T>(() => {
        const value = Storage.getItem<T>(key);

        if (initialValue instanceof Function) {
            return initialValue(value);
        }
        return value || initialValue;
    });

    useEffect(() => {
        Storage.setItem<T>(key, storage);
    }, [storage, key]);

    return { storage, setStorage };
};

export default useLocalStorage;
